import { BadgeCheck, CalendarCheck, FileClock, Search, Target } from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";

import { Card, CardContent, CardHeader, CardTitle } from "@/app/_module/components/ui/card";
import { Input } from "@/app/_module/components/ui/input";
import { leadAssignmentService } from "../services/service";
import { dayLabel, relativeTime } from "./RecentActivity";

interface Milestone {
  id: string;
  type: "appointment_set" | "underwriting" | "sold";
  leadName: string;
  agentName: string;
  agentAgency?: string;
  timestamp: string;
  description: string;
  appointmentAt?: string;
  issuedAt?: string;
}

// Filtered server-side: each chip pages through its own history, so a rare
// Underwriting row is never buried under a week of appointments.
const CHIPS: { key: string; label: string }[] = [
  { key: "all", label: "All" },
  { key: "appointments", label: "Appointments" },
  { key: "underwriting", label: "Underwriting" },
  { key: "sold", label: "Sold & Approved" },
];

const STYLE: Record<Milestone["type"], { icon: ReactNode; row: string }> = {
  appointment_set: { icon: <CalendarCheck className="h-4 w-4 text-blue-600" />, row: "bg-blue-50 border-blue-200" },
  underwriting: { icon: <FileClock className="h-4 w-4 text-amber-600" />, row: "bg-amber-50 border-amber-200" },
  sold: { icon: <BadgeCheck className="h-4 w-4 text-green-600" />, row: "bg-green-50 border-green-200" },
};

const formatTime = (dateString: string) =>
  new Date(dateString).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });

const formatAppointment = (dateString: string) =>
  new Date(dateString).toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

// issued_at is a calendar date (often midnight UTC): compare and print it in UTC, as
// the backend does, or it lands a day early west of Greenwich.
const issuedNote = (m: Milestone) =>
  m.issuedAt && m.issuedAt.slice(0, 10) < new Date(m.timestamp).toISOString().slice(0, 10)
    ? ` · issued ${new Date(m.issuedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" })}`
    : "";

export const MilestoneHistory = function () {
  const [chip, setChip] = useState("all");
  const [search, setSearch] = useState("");

  const query = useInfiniteQuery({
    queryKey: ["lead-assignment", "milestones", chip],
    queryFn: ({ pageParam }) => leadAssignmentService.getMilestones(chip, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextBefore ?? undefined,
  });

  const items = useMemo(
    () => (query.data?.pages.flatMap((p) => p.items) ?? []) as Milestone[],
    [query.data],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((m) =>
      [m.leadName, m.agentName, m.agentAgency, m.description].some((v) => (v || "").toLowerCase().includes(q)),
    );
  }, [items, search]);

  const grouped = useMemo(() => {
    const groups: { day: string; items: Milestone[] }[] = [];
    for (const m of filtered) {
      const day = dayLabel(m.timestamp);
      const last = groups[groups.length - 1];
      if (last && last.day === day) last.items.push(m);
      else groups.push({ day, items: [m] });
    }
    return groups;
  }, [filtered]);

  return (
    <Card className="flex flex-col h-[calc(100vh-220px)] min-h-[480px]">
      <CardHeader className="flex-shrink-0 space-y-2.5 pb-3">
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="text-lg font-semibold">
            Milestones
            <span className="ml-2 text-sm font-normal text-gray-400">{filtered.length}</span>
          </CardTitle>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search milestones..."
              className="h-8 w-[180px] pl-8 text-xs"
            />
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {CHIPS.map((c) => (
            <button
              key={c.key}
              type="button"
              onClick={() => setChip(c.key)}
              className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                chip === c.key
                  ? "bg-primary text-white border-primary"
                  : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </CardHeader>
      <CardContent className="flex-1 overflow-y-auto">
        {query.isError ? (
          <div className="text-center py-8 text-gray-500">
            <p>Couldn&apos;t load milestones.</p>
            <button
              type="button"
              onClick={() => query.refetch()}
              className="mt-3 px-4 py-1.5 rounded-full text-xs font-medium border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
            >
              Try again
            </button>
          </div>
        ) : query.isLoading ? (
          <div className="text-center py-8 text-sm text-gray-500">Loading milestones...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Target className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>{items.length === 0 ? "No appointments or policies yet" : "Nothing matches your search"}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {grouped.map((group) => (
              <div key={group.day}>
                <div className="sticky top-0 z-10 -mx-1 px-1 py-1 bg-white/95 backdrop-blur-sm">
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">{group.day}</span>
                </div>
                <div className="space-y-3 mt-1.5">
                  {group.items.map((m) => (
                    <div key={m.id} className={`flex items-start gap-3 p-3 rounded-lg border ${STYLE[m.type]?.row ?? "bg-gray-50 border-gray-200"}`}>
                      <div className="flex-shrink-0 mt-1">{STYLE[m.type]?.icon}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="font-medium text-gray-900">{m.leadName}</span>
                          <span className="text-gray-500">•</span>
                          <span className="text-sm text-gray-600">{m.agentName}</span>
                          {m.agentAgency && <span className="text-xs text-gray-400">· {m.agentAgency}</span>}
                        </div>
                        <p className="text-sm text-gray-700 mb-1">{m.description}{issuedNote(m)}</p>
                        {m.appointmentAt && (
                          <p className="text-xs text-gray-600 mb-1">For {formatAppointment(m.appointmentAt)}</p>
                        )}
                        <p className="text-xs text-gray-500" title={new Date(m.timestamp).toLocaleString()}>
                          {relativeTime(m.timestamp)} · {formatTime(m.timestamp)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <div className="pt-1 pb-2 text-center">
              {query.hasNextPage ? (
                <button
                  type="button"
                  disabled={query.isFetchingNextPage}
                  onClick={() => query.fetchNextPage()}
                  className="px-4 py-1.5 rounded-full text-xs font-medium border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  {query.isFetchingNextPage ? "Loading older..." : "Load older"}
                </button>
              ) : (
                <span className="text-xs text-gray-400">End of history</span>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
